import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { useCustomer } from '../customer-context.js';
import { formatOwners } from '../lib/owners.js';
import {
  Field,
  Row,
  Section,
  accountHoldsText,
  formatCurrency,
  formatDate,
  statusLabel,
  text,
  useAccountDetail,
  useCifAddress,
} from './account-detail-shared.js';

/**
 * SCRN 52 — Deposit Account Information Overview (CCF-109).
 *
 * Faithful port of the customer's Figma "deposit account info" design: a
 * read-only horizontal header box plus the Maturity/Interest, Charges, DIRT,
 * Statements and Customer-remarks/Compliance sections. The shared agent banner
 * lives in the app shell, so this screen renders only the `<main>` content.
 *
 * The account number arrives as the `?account=` query param from the Dashboard
 * tile. Detail is fetched from the Teller-ID-gated BFF
 * (`POST /api/accounts/details`).
 */
export function DepositAccountScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountNumber = searchParams.get('account') ?? '';
  const { detail, loading, error } = useAccountDetail(accountNumber);

  const cur = detail?.Currency;

  // "Address" field: the customer's **CIF address**, not the per-account Postal
  // Address (issue #638 — consistency with the Account Information screen, #603).
  const cifAddress = useCifAddress();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route and forwards the in-scope account context.
  const onProcessSelect = makeProcessMenuHandler(navigate, accountNumber);

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="deposit-account-heading"
    >
      <div className="mb-4">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="deposit-account-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0"
        >
          Deposit account information
        </h1>
        {/* Process-menu navigation (CCF-109 / Figma): jump to a related
            process. Pure navigation chooser — each route stays independently
            RBAC-gated (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {loading && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          Loading account…
        </p>
      )}

      {detail && (
        <>
          {/* Horizontal header box (CCF-109). */}
          <div className="border-2 border-[#dad7d3] p-6 mb-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 xl:divide-x xl:divide-[#e5e7eb]">
              <div className="xl:pr-6 flex flex-col gap-4">
                <Field label="IBAN" value={text(detail.Iban)} />
                {/* Account holder name(s), CCF-80 (issue #649): a joint account
                    lists ALL owners (comma-separated), from the `Owners` field
                    the BFF overlays from customer/accounts/list — consistent
                    with the Account Information (SCRN-45) overview. Falls back
                    to the single verified banner customer name. */}
                <Field
                  label="Name"
                  value={
                    formatOwners(
                      detail.Owners as string | string[] | undefined,
                    ) ||
                    customer.name ||
                    '—'
                  }
                />
                <Field label="Address" value={text(cifAddress)} />
              </div>
              <div className="xl:px-6 flex flex-col gap-4">
                <Field
                  label="Current balance"
                  value={formatCurrency(detail.Balance, cur)}
                />
                <Field
                  label="Cheque / Mgmt hold"
                  value={formatCurrency(detail.ChequeMgmtHold, cur)}
                />
                <Field
                  label="Available balance"
                  value={formatCurrency(detail.AvailableBalance, cur)}
                />
              </div>
              <div className="xl:px-6 flex flex-col gap-4">
                <Field label="Account holds" value={accountHoldsText(detail)} />
                <Field label="Account type" value="Deposit Account" />
                <Field
                  label="Account status"
                  value={statusLabel(detail.Status)}
                />
              </div>
              <div className="xl:pl-6 flex flex-col gap-4">
                <Field
                  label="Account opened"
                  value={formatDate(detail.OpenedDate)}
                />
                <Field
                  label="Account closed"
                  value={formatDate(detail.ClosedDate)}
                />
              </div>
            </div>
          </div>

          {/* Additional account information — vertical sections (CCF-109). */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <Section title="Maturity / Interest">
              <Row
                label="Maturity date:"
                value={formatDate(detail.MaturityDate)}
              />
              <Row
                label="Notice indicator:"
                value={text(detail.NoticeIndicator)}
              />
              <Row
                label="Accrued interest:"
                value={formatCurrency(detail.AccruedInterest, cur)}
              />
              <Row label="Interest rate:" value={text(detail.InterestRate)} />
              <Row label="Base rate:" value={text(detail.BaseRate)} />
              <Row
                label="Next interest date:"
                value={formatDate(detail.NextInterestDate)}
              />
              <Row
                label="Investment term:"
                value={text(detail.InvestmentTerm)}
              />
              <Row
                label="Last invested date:"
                value={formatDate(detail.LastInvestedDate)}
              />
              <Row
                label="Interest frequency:"
                value={text(detail.InterestFrequency)}
              />
              <Row
                label="Interest paid YTD:"
                value={formatCurrency(detail.InterestPaidYtd, cur)}
              />
              <Row
                label="Interest set off IBAN:"
                value={text(detail.InterestSetOffIban)}
              />
              <Row
                label="Interest set off BIC/Name:"
                value={text(detail.InterestSetOffBic)}
              />
            </Section>

            <div className="flex flex-col gap-6">
              <Section title="Charges">
                <Row
                  label="Available to withdraw without charge:"
                  value={formatCurrency(
                    detail.AvailableToWithdrawWithoutCharge,
                    cur,
                  )}
                />
                <Row
                  label="Penalty amount:"
                  value={formatCurrency(detail.PenaltyAmount, cur)}
                />
              </Section>
              <Section title="DIRT">
                <Row label="DIRT tax code:" value={text(detail.DirtTaxCode)} />
                <Row
                  label="Dirt paid YTD:"
                  value={formatCurrency(detail.DirtTaxPaidYtd, cur)}
                />
              </Section>
            </div>

            <Section title="Statements">
              <Row
                label="Send correspondence:"
                value={text(detail.SendCorrespondence)}
              />
              <Row
                label="Last statement date:"
                value={formatDate(detail.LastStatementDate)}
              />
              <Row
                label="Statement frequency:"
                value={text(detail.StatementFrequency)}
              />
              <Row
                label="Statement cycle:"
                value={text(detail.StatementCycle)}
              />
              <Row
                label="Next statement date:"
                value={formatDate(detail.NextStatementDate)}
              />
              <Row
                label="Last transaction date:"
                value={formatDate(detail.LastTransactionDate)}
              />
            </Section>

            <div className="flex flex-col gap-6">
              <Section title="Customer remarks / Holds">
                <Row
                  label="Customer remarks:"
                  value={text(detail.CustomerRemarksIndicator)}
                />
                <Row
                  label="Customer hold:"
                  value={text(detail.CustomerHoldIndicator)}
                />
              </Section>
              <Section title="Compliance information">
                <Row
                  label="Reason for opening:"
                  value={text(detail.ReasonForOpening)}
                />
                <Row
                  label="Source of funds:"
                  value={text(detail.SourceOfFunds)}
                />
                <Row label="Risk code:" value={text(detail.RiskCode)} />
              </Section>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
